import React, { FC, useMemo, useState } from 'react'
import { Flex, Tag, Typography } from 'antd'
import { MessageOutlined } from '@ant-design/icons'
import { Button, HighlightedTextArea } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { getClassNames } from '../../_utils/classnames'
import { BrandVoiceSummaryChips, BrandVoiceTierKey, InfoRow } from './brand-voice-decomposition'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'
import { CONTENT_DIRECTIVE_FIELDS } from './content-directive-fields'
import {
    ALL_TIER_HIGHLIGHT_STYLES,
    collapsedGuaranteedSpec,
    dedupeSpansForRender,
    findAllTierSpans,
    guaranteedDisplayRows,
    reconcileSpans,
} from './text-highlight'

/**
 * The settings-page view of a saved Brand Voice.
 *
 * Split out of {@link BrandVoiceWell} so it can be rendered and tested without the IoC-bound
 * services the well needs to fetch and edit — this is the piece with the behaviour worth pinning
 * (clamping, the tier filter, what the card leads with), and it was previously unreachable.
 *
 * TEXT LEFT, RULES RIGHT: the same arrangement as the editor drawer, so this reads as a smaller
 * view of the same thing rather than a second, differently-shaped one.
 *
 * What changed and why: the card used to be the raw body and nothing else. On a real directive
 * that is a thousand words of input filling a card whose job is to summarize the OUTPUT — and
 * "what does my Brand Voice actually DO" could only be answered by opening the editor. The text
 * is now clamped, and the Guaranteed rules are shown outright. They are the only tier that can be
 * stated compactly; the other three are prose, and their counts on the chips are the honest
 * summary of them.
 *
 * Heading for each tier's pane. Guaranteed says what it DOES rather than what it is called —
 * "enforced on every generation" is the fact an author is checking the card for.
 */
const TIER_LABEL: Record<BrandVoiceTierKey, string> = {
    guaranteed: 'Enforced on every generation',
    checked: 'Checked by AI review',
    guidance: 'Guidance',
    needsReview: 'Needs review',
}

/** Order the chips are offered in, and the order the default selection falls through. */
const TIER_ORDER: BrandVoiceTierKey[] = ['guaranteed', 'checked', 'guidance', 'needsReview']

interface IBrandVoiceSummary {
    body: string
    /** Null for a directive saved before `compiled_rules` was reliably persisted, or mid-first
     * compile — there is nothing to summarize, so the body renders on its own. */
    compiled: CompiledDirective | null
}

export const BrandVoiceSummary: FC<IBrandVoiceSummary> = ({ body, compiled }) => {
    const itemsByTier = useMemo(
        () => ({
            guaranteed: [],
            checked: compiled?.assertions ?? [],
            guidance: compiled?.style ?? [],
            needsReview: compiled?.unmapped ?? [],
        }),
        [compiled],
    )

    const guaranteedRows = useMemo(() => (compiled ? guaranteedDisplayRows(compiled) : []), [compiled])

    const tierCount = (tier: BrandVoiceTierKey) =>
        tier === 'guaranteed' ? guaranteedRows.length : itemsByTier[tier].length

    /**
     * A tier is ALWAYS selected. That is the trade this layout makes: the chips carry the whole
     * job — they list the tier's rules and highlight its text in one gesture — and the price is
     * that the card no longer has an unhighlighted resting state.
     *
     * Defaults to the first tier that actually has something in it, so a directive with no
     * guarantees opens on whatever it does have rather than on an empty pane.
     */
    const defaultTier = TIER_ORDER.find((t) => tierCount(t) > 0) ?? 'guaranteed'
    const [activeTier, setActiveTier] = useState<BrandVoiceTierKey>(defaultTier)
    const [showFullText, setShowFullText] = useState(false)

    const spans = useMemo(
        // Reconciled against the persisted body. Normally a no-op — a version's body and its
        // compiled rules are written together — but they CAN diverge: the confirm write-path
        // saves the text and deliberately declines to persist rules that no longer match what was
        // reviewed, leaving this card holding an older compile.
        () => (compiled ? reconcileSpans(findAllTierSpans(compiled), body) : []),
        [compiled, body],
    )
    const filteredSpans = useMemo(
        () => dedupeSpansForRender(spans.filter((s) => s.tier === activeTier)),
        [spans, activeTier],
    )

    // Memoized so the array's identity is stable: HighlightedTextArea rebuilds its DOM when this
    // changes, and an inline `.map()` would hand it a new one on every render.
    const activeSpanKeys = useMemo(() => filteredSpans.map((s) => s.key), [filteredSpans])

    const selectTier = (tier: BrandVoiceTierKey) => {
        setActiveTier(tier)
        // Selecting REVEALS the text. The marks for a tier are usually further down than the
        // clamp shows, so without this a chip lit up nothing visible and the hint below was a
        // promise the clamp quietly broke. Not applied to the default selection — the card should
        // still open compact.
        setShowFullText(true)
    }

    if (!compiled) {
        return <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{body}</Typography.Paragraph>
    }

    const activeItems = itemsByTier[activeTier]

    return (
        <Flex vertical gap={12}>
            {/* The chips ARE the control. They already meant "show me this tier" — they filtered
                the text highlighting — and this gives them the rest of that job rather than
                stacking a second mechanism on top. One control that can never disagree with
                itself, which the previous split between chip and list could. */}
            <BrandVoiceSummaryChips compiled={compiled} activeTier={activeTier} onTierToggle={selectTier} />

            {/* TEXT LEFT, RULES RIGHT — the same arrangement as the editor drawer. An earlier
                pass had them flipped, which meant the two views of one directive put the same two
                things on opposite sides: moving between them made the reader re-find both. The
                card summarizing rather than editing is not a reason to move the furniture. */}
            <Flex gap={16} align="flex-start" className={getClassNames('bvw-columns')}>
                <Flex vertical gap={4} className={getClassNames('bvw-column')}>
                    <Typography.Text type="secondary" className={getClassNames('bvw-heading')}>
                        Your text
                    </Typography.Text>
                    {/* No background override here. The tier washes are translucent, so they
                        take their final colour from whatever sits behind them — and this panel
                        used to add a tint the editor's field does not have. Same wash, two
                        grounds, two different colours for the same tier across two views of one
                        directive. Barely visible in light; obvious in dark, where that overlay
                        lightens rather than darkens.

                        Dropping it also lines this panel up with the rule rows beside it, which
                        are plain InfoRows — the text was the only tinted surface in the card. */}
                    <InfoRow icon={<MessageOutlined />}>
                        <div className={getClassNames('bvw-body', { 'sw-v2-bvw-body--clamped': !showFullText })}>
                            <HighlightedTextArea
                                readOnly
                                value={body}
                                spans={filteredSpans}
                                // Every span shown here IS the selection — the chip picked the
                                // whole tier, not one phrase — so all of them get the ring the
                                // editor gives its focused span. Passing null left them washed
                                // but unoutlined, which read as a different treatment of the
                                // same idea across two views of one directive.
                                activeSpanKey={activeSpanKeys}
                                onSpanHover={() => {}}
                                // Clicking a highlighted phrase selects its tier — the reverse of
                                // clicking the chip — so the link works in either direction.
                                onSpanClick={(key) => selectTier(key.split('-')[0] as BrandVoiceTierKey)}
                                tierStyles={ALL_TIER_HIGHLIGHT_STYLES}
                            />
                        </div>
                    </InfoRow>
                    <Flex align="center" justify="space-between" gap={8}>
                        <Typography.Text type="secondary" className={getClassNames('bvw-hint')}>
                            {filteredSpans.length > 0
                                ? `Showing where ${TIER_LABEL[activeTier]} applies.`
                                : `No ${TIER_LABEL[activeTier]} rule points at a specific phrase.`}
                        </Typography.Text>
                        <Button
                            type="link"
                            size="small"
                            className={getClassNames('bvw-toggle')}
                            onClick={() => setShowFullText((v) => !v)}
                        >
                            {showFullText ? 'Show less' : 'Show all'}
                        </Button>
                    </Flex>
                </Flex>
                <Flex vertical gap={6} className={getClassNames('bvw-column')}>
                    <Flex align="center" gap={6}>
                        <span
                            style={{ color: BRAND_VOICE_TIER_VISUAL[activeTier].color, display: 'inline-flex' }}
                            className={getClassNames('bvw-tier-icon')}
                        >
                            {BRAND_VOICE_TIER_VISUAL[activeTier].icon}
                        </span>
                        <Typography.Text type="secondary" className={getClassNames('bvw-heading')}>
                            {TIER_LABEL[activeTier]}
                        </Typography.Text>
                        <Tag bordered={false}>{tierCount(activeTier)}</Tag>
                    </Flex>

                    {/* The REAL PQueryBuilder, disabled — not a hand-rolled row. It owns the field
                        labels, the operator phrasing and the value chips, and reusing it is what
                        keeps this card, the editor drawer and the segment builder saying the same
                        sentence for the same condition.
                        The other three tiers cannot go through it, and that is not a gap: a
                        builder renders a PQuery condition, and those tiers have none. Being
                        unexpressible as a condition is exactly what makes them not-guaranteed, so
                        inventing a condition shape to render them in would misrepresent
                        best-effort guidance as something deterministically checked. They get the
                        same ROW CHROME instead, which is the part that should match. */}
                    {activeTier === 'guaranteed' ? (
                        guaranteedRows.length > 0 ? (
                            <PQueryBuilder
                                value={collapsedGuaranteedSpec(compiled)}
                                fields={CONTENT_DIRECTIVE_FIELDS}
                                disabled
                                className={getClassNames('bvd-guaranteed-pqb')}
                                onRuleCommit={() => {}}
                                labels={{ connectors: { and: '', or: '' } }}
                            />
                        ) : (
                            <Typography.Text type="secondary" className={getClassNames('bvw-hint')}>
                                Nothing is deterministically enforced yet.
                            </Typography.Text>
                        )
                    ) : activeItems.length > 0 ? (
                        // Wrapped and in full. Showing one tier at a time is what buys the room
                        // for that — the previous card listed all three at once and had to
                        // truncate every rule to a single line, which made them unreadable
                        // without making the card much shorter.
                        // Same row chrome the editor drawer gives these, and the same chrome
                        // PQueryBuilder gives a guaranteed rule: a bordered row at body size.
                        // Bare quoted text at 12px read as a heavy block of small print rather
                        // than as a list of rules, and it made the two halves of one card look
                        // like two unrelated things.
                        <Flex vertical gap={8}>
                            {activeItems.map((item, i) => (
                                <InfoRow key={i}>
                                    <Typography.Text>{item.text}</Typography.Text>
                                </InfoRow>
                            ))}
                        </Flex>
                    ) : (
                        <Typography.Text type="secondary" className={getClassNames('bvw-hint')}>
                            Nothing in this tier.
                        </Typography.Text>
                    )}
                </Flex>
            </Flex>
        </Flex>
    )
}
